<script setup lang="ts" generic="T extends PanelMenuItem<T>">
import { ref, computed, watch } from 'vue'
import SvgIcon from '@/components/SvgIcon.vue'
import { useStableEnter } from '@/composables/useStableEnter'
import type { PanelMenuItem } from './type'

export type PanelMenuExpandedKeys = Record<string, boolean>

/** —— Props/Emits —— */
const props = defineProps<{
  model: T[]
  expandedKeys?: PanelMenuExpandedKeys | null
  collapsed?: boolean
  itemRef?: (item: T, el: HTMLElement | null) => void
  pt?: any
}>()

const emit = defineEmits<{
  (e: 'update:expandedKeys', v: PanelMenuExpandedKeys): void
  (e: 'item-click', item: T, event: MouseEvent): void
  (e: 'collapsed-item-click', item: T, event: MouseEvent): void
  (e: 'item-hover', item: T, event: MouseEvent): void
}>()

/** —— 受控/非受控 —— */
const isControlled = computed(() => props.expandedKeys !== undefined)
const uncontrolledExpanded = ref<PanelMenuExpandedKeys>({})
const currentExpandedKeys = computed(() =>
  isControlled.value ? (props.expandedKeys ?? {}) : uncontrolledExpanded.value
)
const onExpandedChange = (next: PanelMenuExpandedKeys) => {
  const v = { ...(next ?? {}) }
  if (!isControlled.value) uncontrolledExpanded.value = v
  // console.log('onExpandedChange', toRaw(currentExpandedKeys.value))
  emit('update:expandedKeys', v)
}
watch(
  () => props.collapsed,
  (v) => {
    if (v) {
      if (isControlled.value) emit('update:expandedKeys', {})
      else uncontrolledExpanded.value = {}
    }
  }
)
/** —— 工具 —— */
const stableEnter = useStableEnter({ delay: 150 })

/** —— 依赖 —— */
const isExpanded = (key?: string) => !!(key && currentExpandedKeys.value[key])
const toggleKey = (key: string, expand: boolean) => {
  const next = { ...currentExpandedKeys.value }
  if (expand) next[key] = true
  else delete next[key]
  onExpandedChange(next)
}

const shouldBlockExpandKeys = (e: KeyboardEvent) => {
  if (!props.collapsed) return false
  const k = e.key
  return k === 'Enter' || k === ' ' || k === 'ArrowLeft' || k === 'ArrowRight'
}
const onItemKeydown = (e: KeyboardEvent, item: T, hasSubmenu: boolean) => {
  if (shouldBlockExpandKeys(e)) {
    e.preventDefault()
    e.stopPropagation()
    return
  }
  if (!props.collapsed && (e.key === 'Enter' || e.key === ' ')) {
    e.preventDefault()
    ;(e.currentTarget as HTMLElement)?.click()
  }
}

const handleItemMouseEnter = (event: MouseEvent, item: T) => {
  const el = event.currentTarget as HTMLElement | null
  if (item.key && el) stableEnter.reTarget(item.key, el, () => emit('item-hover', item, event))
}
const handleItemMouseLeave = (_event: MouseEvent, item: T) => {
  if (item.key) stableEnter.cancel(item.key)
}

const handleClick = (e: MouseEvent, item: T, hasSubmenu: boolean) => {
  if (props.collapsed && hasSubmenu) {
    emit('collapsed-item-click', item, e)
    return
  }
  if (hasSubmenu && item.key) {
    toggleKey(item.key, !isExpanded(item.key))
    return
  }
  emit('item-click', item, e)
}
</script>

<template>
  <div :class="[styles.root, props.pt?.root]" v-bind="$attrs">
    <!-- 顶层列表 -->
    <ul :class="styles.rootList">
      <template v-for="(item, idx) in model" :key="item.key ?? `k-${idx}`">
        <!-- Divider -->
        <li v-if="item.separator" role="separator" :class="[item.class, styles.separator]" />

        <!-- 普通项 -->
        <li
          v-else
          :class="[
            styles.panel,
            props.pt?.panel,
            { [styles.expanded]: item.key && currentExpandedKeys[item.key] },
            { [styles.isCur]: item?.active, [styles.isCollapse]: !!collapsed }
          ]">
          <!-- header 可点击主体 -->
          <!-- 外部可保存特定菜单项的锚点 DOM -->
          <div
            :ref="(el) => props.itemRef?.(item, el as HTMLElement | null)"
            :class="[
              styles.header,
              {
                [styles.isCur]: item?.active,
                [styles.isCollapse]: !!collapsed
              }
            ]"
            @click.prevent="handleClick($event, item, !!item.items?.length)"
            @keydown="onItemKeydown($event as KeyboardEvent, item, !!item.items?.length)"
            @mouseenter="collapsed ? handleItemMouseEnter($event, item) : null"
            @mouseleave="collapsed ? handleItemMouseLeave($event, item) : null">
            <div :class="[styles.headerContent]">
              <slot name="header-icon" :item="item" :active="!!item.active" :styles="styles">
                <span
                  v-if="item.iconUrl"
                  aria-hidden="true"
                  :class="[styles.icon, props.pt?.headerIcon]"
                  :style="{
                    backgroundImage: `url(${item.active && item.highlightIconUrl ? item.highlightIconUrl : item.iconUrl})`,
                    backgroundRepeat: 'no-repeat'
                  }" />
              </slot>
              <!-- 文本（收起时不显示） -->
              <span v-if="!collapsed" :class="styles.headerLabel">
                {{ typeof item.label === 'function' ? item.label(item) : (item.label ?? '') }}
              </span>
              <!-- 目前用于红点 -->
              <slot
                name="header-extra"
                :item="item"
                :active="!!item.active"
                :collapsed="!!collapsed"
                :styles="styles" />
              <!-- 子菜单箭头（未收起时显示） -->
              <span
                v-if="!!item.items?.length && !collapsed"
                :class="[styles.submenuIcon, { [styles.isOpen]: isExpanded(item.key) }]"
                aria-hidden="true">
                <SvgIcon name="svg/down_arrow.svg" size="12px" :class="styles.rotate" />
              </span>
            </div>
          </div>

          <!-- 子树 -->
          <Transition name="base-pm">
            <div v-if="!collapsed && isExpanded(item.key)" class="base-pm-content">
              <div :class="styles.rootListWrp">
                <ul :class="[styles.subItemWrp, props.pt?.subItemWrp]">
                  <template
                    v-for="(child, cIdx) in item.items"
                    :key="child.key ?? `ck-${idx}-${cIdx}`">
                    <!-- Divider in children -->
                    <li
                      v-if="child.separator"
                      role="separator"
                      :class="[child.class, styles.separator]" />
                    <slot
                      v-else
                      :name="child?.type ? `child-li-${child.type}` : 'child-li'"
                      :item="child"
                      :parent="item"
                      :collapsed="!!collapsed"
                      :active="!!child.active"
                      :hasSubmenu="!!child.items?.length"
                      :liClass="[styles.item, props.pt?.item]"
                      :liListeners="{
                        click: (e: MouseEvent) => handleClick(e, child, !!child.items?.length),
                        keydown: (e: KeyboardEvent) =>
                          onItemKeydown(e, child, !!child.items?.length),
                        mouseenter: (e: MouseEvent) =>
                          collapsed ? handleItemMouseEnter(e, child) : null,
                        mouseleave: (e: MouseEvent) =>
                          collapsed ? handleItemMouseLeave(e, child) : null
                      }">
                      <li
                        :class="[styles.item, props.pt?.item]"
                        @click.prevent="handleClick($event, child, !!child.items?.length)"
                        @keydown="
                          onItemKeydown($event as KeyboardEvent, child, !!child.items?.length)
                        "
                        @mouseenter="collapsed ? handleItemMouseEnter($event, child) : null"
                        @mouseleave="collapsed ? handleItemMouseLeave($event, child) : null">
                        <div
                          :class="[
                            props.pt?.itemContent,
                            { [styles.isCur]: child?.active, [styles.isCollapse]: !!collapsed }
                          ]">
                          <!-- 单项插槽：若提供 #child，则只接管“内容区”；否则走默认内容 -->
                          <slot
                            name="child"
                            :item="child"
                            :parent="item"
                            :level="1"
                            :active="!!child.active"
                            @click.prevent="handleClick($event, child, !!child.items?.length)">
                            <!-- 默认内容 -->
                            <span
                              v-if="child.iconUrl"
                              aria-hidden="true"
                              :class="[styles.icon, props.pt?.itemIcon]"
                              :style="{
                                backgroundImage: `url(${child.iconUrl})`,
                                backgroundRepeat: 'no-repeat'
                              }" />
                            <span v-if="!collapsed" :class="styles.itemLabel">
                              {{
                                typeof child.label === 'function'
                                  ? child.label(child)
                                  : (child.label ?? '')
                              }}
                            </span>
                          </slot>
                        </div>
                      </li>
                    </slot>
                  </template>
                </ul>
              </div>
            </div>
          </Transition>
        </li>
      </template>
    </ul>
  </div>
</template>

<style lang="scss" module="styles">
.root {
  display: flex;
  flex-direction: column;
  --pm-header-height: 40px;
}

.rootList {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.panel {
  margin: 0 14px;

  &.expanded {
    background-color: var(--pm-expand-panel-bg, transparent);
  }
}

// 一级item
.header {
  cursor: pointer;
  display: flex;
  border-radius: 10px;
  color: #eee;
  transform: translateZ(0);
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  backface-visibility: hidden;
  will-change: transform;
  height: var(--pm-header-height, 40px);
  // height: 40px;
}

.headerContent {
  display: flex;
  align-items: center;
  position: relative;
  flex: 1;
  border-radius: 10px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #232323;
}

/* // 二级根节点 */
.subItemWrp {
  display: flex;
  flex-direction: column;
}

.item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: var(--pm-item-padding, 10px);
  text-decoration: none;
  cursor: pointer;
  // transform: translateZ(0);
  // backface-visibility: hidden;
  // will-change: transform;
}

// .item:hover {
//   transform: scale(1.02) translateZ(0);
// }
.headerLabel {
  font-weight: 400;
  color: var(--pm-label-header-color, #eee);
  // font-size: var(--pm-label-header-font-size, 14px);
  font-size: 14px;
}

.itemLabel {
  color: var(--pm-label-item-color, #eee);
  line-height: var(--pm-label-item-line-height, 20px);
  font-size: var(--pm-label-item-font-size, 16px);
}

.submenuIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--pm-submenu-icon-size, 16px);
  height: var(--pm-submenu-icon-size, 16px);
  transition: transform 400ms cubic-bezier(0.2, 0.8, 0.2, 1);
  width: 26px;
  height: 26px;
  border: 0.5px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  color: #bbbbbb;
  margin-left: auto;
}

.submenuIcon.isOpen {
  color: #ffffff;

  .rotate {
    transition: transform 400ms cubic-bezier(0.2, 0.8, 0.2, 1);
    transform: rotate(180deg);
  }
}

.icon {
  display: inline-flex;
  width: var(--pm-icon-size);
  height: var(--pm-icon-size);
  font-size: var(--pm-icon-size);
  color: var(--pm-icon-color, currentColor);
  color: var(--pm-icon-color, currentColor);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
}

/* 收起时的一些UI */
.isCollapse {
  &.panel {
    background: none;
  }

  .header {
    width: 44px;
    height: 44px;
  }

  .headerContent {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #232323;
    border-radius: 10px;
    padding: 0;
  }

  .icon {
    display: inline-flex;
    background-size: contain;
  }
}

// hover和路由匹配的item高亮
.header:not(.isCur):hover {
  .submenuIcon {
    color: #ffffff;
  }
}

.header:hover {
  transform: scale(1.02) translateZ(0);
}

.header:hover,
.header.isCur {
  .headerContent {
    background: #3a352c;
  }
}
</style>
<style>
.base-pm-enter-from,
.base-pm-leave-to {
  max-height: 0;
}

.base-pm-enter-active {
  overflow: hidden;
  transition: max-height 1s ease-in-out;
}

.base-pm-leave-active {
  overflow: hidden;
  transition: max-height 450ms cubic-bezier(0, 1, 0, 1);
}

.base-pm-enter-to,
.base-pm-leave-from {
  max-height: 1000px;
}
</style>
